import { useState, type ReactNode } from 'react';
import { Sparkles, Mail, Flame, CalendarCheck, Trophy } from 'lucide-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Sidebar } from '@/components/layout/sidebar';
import { DashboardHero } from '@/components/dashboard/dashboard-hero';
import { StatCard } from '@/components/dashboard/stat-card';
import { PipelineFunnel } from '@/components/dashboard/pipeline-funnel';
import { AISignalChip } from '@/components/ai-signal';
import {
  useDashboardStats,
  useClosingDeals,
  useFunnel,
  useHotDeals,
  type HotDeal,
} from '@/features/dashboard/queries';
import { useLeaderboard } from '@/features/leaderboard/queries';
import { useCurrentPipeline } from '@/features/preferences/use-pipeline';
import { formatCurrency, formatCurrencyShort, getInitials } from '@/lib/format';

export const Route = createFileRoute('/_auth/dashboard')({
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const { pipeline } = useCurrentPipeline();
  const stats = useDashboardStats(pipeline);
  const closing = useClosingDeals(pipeline);
  const funnel = useFunnel(pipeline);
  const hotDealsQ = useHotDeals(pipeline);
  const leaderboardQ = useLeaderboard();
  const [insightDismissed, setInsightDismissed] = useState(false);

  const closingDeals = closing.data ?? [];
  const closingValue = closingDeals.reduce((s, d) => s + d.dealValue, 0);
  const hotDeals: HotDeal[] = hotDealsQ.data ?? [];
  const topInsight = hotDeals[0];

  const topThreeAgents = (leaderboardQ.data ?? [])
    .slice()
    .sort((a, b) => (b.closes_month ?? 0) - (a.closes_month ?? 0))
    .slice(0, 3);

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto" data-testid="dashboard-main">
        <DashboardHero
          closingCount={closingDeals.length}
          closingValueLabel={formatCurrencyShort(closingValue)}
          hotLeadsCount={(stats.data?.openDeals ?? 0) > 0 ? Math.min(3, stats.data?.openDeals ?? 0) : 0}
        />

        <div className="grid grid-cols-2 gap-3 px-9 pt-5 sm:grid-cols-4">
          <StatCard
            label="Total Pipeline"
            value={formatCurrencyShort(stats.data?.pipelineValue ?? 0)}
            loading={stats.isLoading}
          />
          <StatCard
            label="Open Deals"
            value={(stats.data?.openDeals ?? 0).toString()}
            loading={stats.isLoading}
          />
          <StatCard
            label="Won this Month"
            value={(stats.data?.wonCount ?? 0).toString()}
            loading={stats.isLoading}
          />
          <StatCard
            label="Revenue MTD"
            value={formatCurrencyShort(stats.data?.wonRevenue ?? 0)}
            loading={stats.isLoading}
          />
        </div>


        <div className="grid grid-cols-1 gap-5 px-9 pb-9 pt-5 lg:grid-cols-[1.4fr_1fr]">
          <PipelineFunnel
            stages={funnel.data ?? []}
            loading={funnel.isLoading}
          />

          <div className="flex flex-col gap-5">
            {/* AI Insight card — top of right column, conditional on a hot deal */}
            {topInsight && !insightDismissed ? (
              <section
                className="rounded-xl border border-accent/30 bg-gradient-to-br from-accent/[0.08] to-accent/[0.02] p-5"
                data-testid="ai-insight-card"
              >
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-accent" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent">
                    SoftSync
                  </span>
                </div>
                <p className="text-[13.5px] leading-snug text-text">
                  <strong className="font-semibold">{topInsight.companyName}</strong> {topInsight.insight.toLowerCase()}
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void navigate({ to: '/pipeline' })}
                    className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-[11.5px] font-semibold text-accent-fg transition-transform hover:-translate-y-px"
                  >
                    <Mail className="h-3 w-3" />
                    Draft email
                  </button>
                  <button
                    type="button"
                    onClick={() => setInsightDismissed(true)}
                    className="rounded border border-border/[0.15] bg-transparent px-3 py-1.5 text-[11.5px] text-text-dim transition-colors hover:border-border-hi hover:text-text"
                  >
                    Dismiss
                  </button>
                </div>
              </section>
            ) : null}

            <section className="rounded-xl border border-border/[0.08] bg-surface-2 p-5" data-testid="closing-panel">
              <div className="mb-3 flex items-center gap-2">
                <CalendarCheck className="h-4 w-4 text-accent" aria-hidden />
                <h2 className="text-sm font-semibold text-text">Closing this week</h2>
              </div>
              {closing.isLoading ? (
                <EmptyHint icon={<CalendarCheck className="h-4 w-4" />} loading />
              ) : closingDeals.length === 0 ? (
                <EmptyHint
                  icon={<CalendarCheck className="h-4 w-4" />}
                  title="Nothing forecast to close yet"
                  body="High-probability deals from the pipeline appear here once they're in Proposal or Negotiation."
                />
              ) : (
                <div className="flex flex-col">
                  {closingDeals.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="flex items-center gap-2.5 border-b border-border/[0.08] py-2.5 text-left transition-all duration-fast last:border-b-0 hover:-translate-y-px hover:border-border-hi hover:bg-surface-hi hover:shadow-[0_4px_16px_-8px_rgb(var(--accent)/0.25)]"
                      onClick={() => void navigate({ to: '/pipeline' })}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-medium text-text">{d.companyName}</div>
                        <div className="text-[11px] text-text-mute">
                          {d.probability}% · {d.stage}
                        </div>
                      </div>
                      <span className="font-display text-sm font-semibold text-text">
                        {formatCurrency(d.dealValue)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Hot deals — heuristic-scored hot/champion */}
            <section className="rounded-xl border border-border/[0.08] bg-surface-2 p-5" data-testid="hot-deals-panel">
              <div className="mb-3 flex items-center gap-2">
                <Flame className="h-4 w-4 text-accent" aria-hidden />
                <h2 className="text-sm font-semibold text-text">Hot deals</h2>
              </div>
              {hotDealsQ.isLoading ? (
                <EmptyHint icon={<Flame className="h-4 w-4" />} loading />
              ) : hotDeals.length === 0 ? (
                <EmptyHint
                  icon={<Flame className="h-4 w-4" />}
                  title="No hot deals right now"
                  body="SoftSync flags deals as hot when they combine high value, advancing probability, and recent activity."
                />
              ) : (
                <div className="flex flex-col">
                  {hotDeals.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => void navigate({ to: '/pipeline' })}
                      className="flex items-center gap-2.5 border-b border-border/[0.08] py-2.5 text-left transition-all duration-fast last:border-b-0 hover:-translate-y-px hover:border-border-hi hover:bg-surface-hi hover:shadow-[0_4px_16px_-8px_rgb(var(--accent)/0.25)]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-text">{d.companyName}</div>
                        <div className="text-[11px] text-text-mute">{d.probability}%</div>
                      </div>
                      <AISignalChip signal={d.signal} />
                      <span className="font-display text-sm font-semibold text-text">
                        {formatCurrencyShort(d.dealValue)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Mini Leaderboard — top 3 by closes_month */}
            <section className="rounded-xl border border-border/[0.08] bg-surface-2 p-5" data-testid="mini-leaderboard">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-accent" aria-hidden />
                  <h2 className="text-sm font-semibold text-text">This month</h2>
                </div>
                <button
                  type="button"
                  onClick={() => void navigate({ to: '/leaderboard' })}
                  className="text-[11.5px] text-accent transition-opacity hover:opacity-80"
                >
                  View all
                </button>
              </div>
              {leaderboardQ.isLoading ? (
                <EmptyHint icon={<Trophy className="h-4 w-4" />} loading />
              ) : topThreeAgents.length === 0 ? (
                <EmptyHint
                  icon={<Trophy className="h-4 w-4" />}
                  title="No active agents yet"
                  body="Top performers will be listed here once they start booking demos and closing deals this month."
                />
              ) : (
                <div className="flex flex-col">
                  {topThreeAgents.map((a, i) => (
                    <div
                      key={a.agent_id ?? `row-${i}`}
                      className="flex items-center gap-2.5 border-b border-border/[0.08] py-2.5 transition-all duration-fast last:border-b-0 hover:-translate-y-px hover:border-border-hi hover:bg-surface-hi hover:shadow-[0_4px_16px_-8px_rgb(var(--accent)/0.25)]"
                    >
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full font-mono text-[10px] ${
                          i === 0 ? 'bg-accent/20 text-accent font-semibold' : 'bg-surface-hi text-text-dim'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent">
                        {getInitials(a.full_name)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                        {a.full_name ?? '—'}
                      </span>
                      <span className="font-mono text-sm text-text">{a.closes_month ?? 0}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

interface EmptyHintProps {
  icon: ReactNode;
  title?: string;
  body?: string;
  loading?: boolean;
}

/**
 * Soft empty/loading state for dashboard list panels. A dashed border + hint
 * line is more honest than a single-line "No X yet" when screen-sharing — it
 * shows the panel is wired and explains what would appear once data lands.
 */
function EmptyHint({ icon, title, body, loading }: EmptyHintProps): JSX.Element {
  if (loading) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-dashed border-border/[0.10] bg-surface/40 px-4 py-3" aria-hidden>
        <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-text-mute">{icon}</div>
        <div className="flex-1">
          <div className="h-3 w-32 animate-pulse rounded bg-surface-hi/60" />
          <div className="mt-1.5 h-2.5 w-48 animate-pulse rounded bg-surface-hi/40" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 rounded-md border border-dashed border-border/[0.12] bg-surface/40 px-4 py-3">
      <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center text-text-mute">{icon}</div>
      <div>
        {title && <div className="text-[12.5px] font-medium text-text">{title}</div>}
        {body && <p className="mt-0.5 text-[11.5px] leading-snug text-text-dim">{body}</p>}
      </div>
    </div>
  );
}
